import { Charge, Partner, ShoppingCart } from 'app/api/@generated/backend';

import { DataLayerStaticData } from 'app/data-layers/static-data/data-layer-static-data';
import { ShoppingCartUtils } from 'app/utils/shopping-cart-utils';
import { OrderStaticData } from 'app/static-data/order-static-data';
import { DataLayerEcomItem } from 'app/data-layers/model/data-layer-event';
import { HouseType } from 'app/pages/order/model/house-type';
import { OrderService } from 'app/pages/order/services/order-service';
import { i18n } from 'app/i18n';

export const getAllPurchasedItemsForSingleUnitScenario = (
    shoppingCart: ShoppingCart,
    partner?: Partner,
): DataLayerEcomItem[] => {
    const additionalItems = getAdditionalPurchasedItems(shoppingCart, partner);

    return [...additionalItems, getExtraUnitItem(shoppingCart, partner)];
};

export const getAllPurchasedItemsForSelectedHousingTypeScenario = (
    shoppingCart: ShoppingCart,
    houseType: HouseType,
    isExtraAdded: boolean,
    partner?: Partner,
): DataLayerEcomItem[] => {
    const additionalItems = getAdditionalPurchasedItems(shoppingCart, partner);

    return [
        ...additionalItems,
        getSelectedHouseData(shoppingCart, houseType, partner),
        ...(isExtraAdded ? [getExtraUnitItem(shoppingCart, partner)] : []),
    ];
};

export const getSelectedHouseData = (shoppingCart: ShoppingCart, houseType: HouseType, partner?: Partner) => {
    const numberOfWifiUnits = OrderService.getNumberOfWifiUnitsByHouseType(houseType);
    const selectedWifiOption = ShoppingCartUtils.getSelectedWifiOption(shoppingCart, numberOfWifiUnits);
    const {
        wifiUnitInformation: { displayName, code, category },
    } = shoppingCart;

    return withDefaultEcomItemValues(
        {
            item_name: displayName || '',
            item_id: code,
            currency: selectedWifiOption.recurringCharge.currency,
            index: Object.keys(HouseType).indexOf(houseType),
            item_variant: i18n.product.name[houseType],
            price: selectedWifiOption.firstYearPriceForUnits,
            quantity: numberOfWifiUnits,
            item_category1: DataLayerStaticData.EQUIPMENT_CATEGORY,
            item_category2: DataLayerStaticData.SERVICE_CATEGORY,
            item_category3: category,
            item_product_type: DataLayerStaticData.ITEM_PRODUCT_TYPE,
        },
        partner,
    );
};

export const getExtraUnitItem = (shoppingCart: ShoppingCart, partner?: Partner) => {
    const selectedWifiOption = ShoppingCartUtils.getSelectedWifiOption(shoppingCart, OrderStaticData.EXTRA_UNIT_AMOUNT);
    const {
        wifiUnitInformation: { displayName, code, category },
    } = shoppingCart;

    return withDefaultEcomItemValues(
        {
            item_name: displayName || '',
            item_id: code,
            currency: selectedWifiOption.recurringCharge.currency,
            index: 2,
            item_variant: i18n.product.name.EXTRA_WIFI_UNIT,
            price: selectedWifiOption.firstYearPriceForUnits,
            quantity: OrderStaticData.EXTRA_UNIT_AMOUNT,
            item_category1: DataLayerStaticData.EQUIPMENT_CATEGORY,
            item_category2: DataLayerStaticData.SERVICE_CATEGORY,
            item_category3: category,
            item_product_type: DataLayerStaticData.ITEM_PRODUCT_TYPE,
        },
        partner,
    );
};

const getAdditionalPurchasedItems = (shoppingCart: ShoppingCart, partner?: Partner): DataLayerEcomItem[] =>
    shoppingCart.establishmentCharge ? [getEstablishmentDataItem(shoppingCart.establishmentCharge, partner)] : [];

const getEstablishmentDataItem = (establishmentCharge: Charge, partner?: Partner) =>
    withDefaultEcomItemValues(
        {
            item_id: establishmentCharge.code,
            item_name: establishmentCharge.invoiceText,
            currency: establishmentCharge.currency,
            index: 0,
            item_category1: establishmentCharge.invoiceText,
            item_category2: DataLayerStaticData.ITEM_PRODUCT_TYPE,
            item_variant: establishmentCharge.invoiceText,
            price: establishmentCharge.price,
            quantity: 1,
            item_product_type: establishmentCharge.invoiceText,
        },
        partner,
    );

const defaultEcomItemValues: Pick<
    DataLayerEcomItem,
    | 'affiliation'
    | 'item_brand'
    | 'item_category4'
    | 'item_list_id'
    | 'item_list_name'
    | 'item_binding'
    | 'item_business_unit'
> = {
    affiliation: window.location.hostname,
    item_brand: DataLayerStaticData.PRODUCT_BRAND,
    item_category4: '',
    item_list_id: '',
    item_list_name: '',
    item_binding: i18n.product.binding.required,
    item_business_unit: DataLayerStaticData.BUSINESS_UNIT,
};

const withDefaultEcomItemValues = (
    item: Omit<DataLayerEcomItem, keyof typeof defaultEcomItemValues | 'partner_id' | 'partner_name'>,
    partner?: Partner,
): DataLayerEcomItem => ({
    ...defaultEcomItemValues,
    partner_id: partner?.id || '',
    partner_name: partner?.name || '',
    ...item,
});
